import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { JsonViewer } from '@dataxpdtn/mui-json-viewer';
import { existsFilter } from '@iotv/core-services';
import { Device, getNiceDate, prettifyHex } from '@iotv/datamodel';
import { btoaHex, LinkType } from '@iotv/iotv-v3-types';
import { decodeDownlinkPayload, decodeUplinkPayload, discoverPayloadDefinitionsFromPayloadStr, resolvePayloadDefinitions } from '@iotv/payload-definition';
import InfoIcon from '@mui/icons-material/Info';
import { IconButton, InputAdornment, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { ConsumerDao } from '../../../../data/ConsumerDAO';
import { ActionIcons } from '../../../../Iconography';
import { AutosizedTextField } from '../../../common/AutosizedTextField';
import { fontSize } from '../../../NetworkSystem/cellFormatters/fontSize';
const debug = process.env.REACT_APP_DEBUG && false;
export const PayloadDataGridCell = (p) => {
    return typeof p.value === 'string' ? (_jsx(_Fragment, { children: _jsxs(Typography, { style: { alignItems: 'center', display: 'flex', flexDirection: 'row', fontSize }, children: [_jsx(InfoIcon, { color: "info", fontSize: "small", sx: { marginInlineEnd: 1 } }), prettifyHex(p.value)] }) })) : undefined;
};
export const DecodedPayloadPopUp = ({ timestamp, payloadStr, linkType, device: deviceIn, port }) => {
    let unpacked = undefined;
    debug && console.log(`decoding`, { payloadStr, linkType });
    const [device, setDevice] = useState(new Device(deviceIn));
    useEffect(() => {
        if (deviceIn instanceof Device)
            return;
        ConsumerDao.getOne(deviceIn).then(({ err, data }) => {
            if (err || !data)
                return;
            setDevice(new Device(data));
        });
    }, [deviceIn.sk]);
    let matchingPayloadDefinitions = [];
    matchingPayloadDefinitions = resolvePayloadDefinitions({ rawPayloadStr: payloadStr, device, port, linkType });
    if (matchingPayloadDefinitions.length === 0) {
        console.warn('Falling back to discovery-based payload definition resolution method!');
        matchingPayloadDefinitions = discoverPayloadDefinitionsFromPayloadStr(payloadStr, port).matchingPayloadDefinitions;
    }
    if (0 < matchingPayloadDefinitions.length) {
        if (linkType === LinkType.UP) {
            debug && console.log('Decoding uplink');
            unpacked = decodeUplinkPayload({ rawPayloadStr: payloadStr, payloadDefinition: matchingPayloadDefinitions[0], port });
        }
        else if (linkType === LinkType.DOWN) {
            debug && console.log('Decoding downlink');
            unpacked = decodeDownlinkPayload({ rawPayloadStr: payloadStr, payloadDefinition: matchingPayloadDefinitions[0], port: null });
        }
        else {
            console.warn('Unsupported linkType!', linkType);
        }
    }
    const payloadDefinitionNames = matchingPayloadDefinitions.map((d) => d.name).filter(existsFilter);
    return (_jsxs(Stack, { gap: 1, children: [_jsxs(Stack, { direction: "row", paddingBlockStart: 1, flexWrap: "wrap", gap: 1, children: [_jsx(AutosizedTextField, { label: "Device EUI", value: prettifyHex(device.eui ?? device.id), size: "small", multiline: true, slotProps: {
                            input: {
                                endAdornment: (_jsx(InputAdornment, { position: "end", children: _jsx(IconButton, { onClick: () => navigator.clipboard.writeText((device.eui ?? device.id).toUpperCase()), children: _jsx(ActionIcons.CopyToClipboard, {}) }) })),
                            },
                        } }, "eui"), _jsx(AutosizedTextField, { label: "Message bytes", value: prettifyHex(payloadStr), size: "small", multiline: true, slotProps: {
                            input: {
                                endAdornment: (_jsx(InputAdornment, { position: "end", children: _jsx(IconButton, { onClick: () => navigator.clipboard.writeText(payloadStr.toUpperCase()), children: _jsx(ActionIcons.CopyToClipboard, {}) }) })),
                            },
                        } }, "bytes"), _jsx(AutosizedTextField, { label: "Message b64", value: btoaHex(payloadStr), size: "small", multiline: true, slotProps: {
                            input: {
                                endAdornment: (_jsx(InputAdornment, { position: "end", children: _jsx(IconButton, { onClick: () => navigator.clipboard.writeText(btoaHex(payloadStr) ?? ''), children: _jsx(ActionIcons.CopyToClipboard, {}) }) })),
                            },
                        } }, "b64"), payloadDefinitionNames.map((name, i) => (_jsx(AutosizedTextField, { label: "Matching definition", value: name, size: "small", color: i === 0 ? 'primary' : undefined, multiline: true, slotProps: {
                            input: {
                                sx: { opacity: i === 0 ? undefined : 0.5 },
                                endAdornment: (_jsx(InputAdornment, { position: "end", children: _jsx(IconButton, { onClick: () => navigator.clipboard.writeText(name.toUpperCase()), children: _jsx(ActionIcons.CopyToClipboard, {}) }) })),
                            },
                        } }, `${i}_${name}`)))] }), _jsx(Stack, { direction: "row", paddingBlockStart: 1, flexWrap: "wrap", gap: 1, children: timestamp && (_jsx(AutosizedTextField, { value: getNiceDate(timestamp),
                    label: 'Timestamp',
                    size: 'small',
                    multiline: true,
                    slotProps: {
                        input: {
                            endAdornment: (_jsx(InputAdornment, { position: "end", children: _jsx(IconButton, { onClick: () => navigator.clipboard.writeText(timestamp.toString()), children: _jsx(ActionIcons.CopyToClipboard, {}) }) })),
                        },
                    } }, `timestamp-${timestamp}`)) }), _jsx(JsonViewer, { value: unpacked, rootName: device.eui, editable: false, displaySize: false, displayComma: false, quotesOnKeys: false, objectSortKeys: false, enableClipboard: true, displayDataTypes: false, defaultInspectDepth: 2, groupArraysAfterLength: undefined, collapseStringsAfterLength: 200, sx: { fontSize: '1.15em' } })] }));
};
